<template>
  <v-form :disabled="disabled">
    <v-card :outlined="outlined">
      <v-card-title class="py-1">
        {{ title }}
      </v-card-title>
      <v-container fluid>
        <v-row v-if="dispositionCdItems.length > 0">
          <v-col cols="3">
            <SelectSingleInput
              :vid="getVID('dispositionCd')"
              v-model="dispositionCdComputed"
              :items="dispositionCdItems"
              itemText="name"
              itemValue="value"
              :label="$t('ELE0440')"
              :required="canEditDisposition()"
              :disabled="!canEditDisposition()"
            />
          </v-col>
          <v-col cols="6">
            <TextFieldInput
              v-if="canEditDisposition()"
              :label="$t('ELE0489')"
              v-model="dispositionReasonComputed"
              :maxLength="1000"
            />
            <TextFieldRef v-else :label="$t('ELE0489')" :value="dispositionReasonComputed" />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card outlined>
              <v-data-table
                :headers="headersRoute"
                :items="checkersComputed"
                dense
                disable-pagination
                hide-default-footer
                mobile-breakpoint="0"
              >
                <template v-slot:[`item.by`]="{ item }">
                  {{ getLabelBy(item.checkedCat) }}
                </template>
                <template v-slot:[`item.checkerCd`]="{ item, index }">
                  <UserInput v-if="!canEditChecker(index)" label="" v-model="item.checkerCd" disabled />
                  <UserInput
                    v-else
                    :vid="getDtlVID('checkerCd', index)"
                    label=""
                    v-model="item.checkerCd"
                    :companyCd="checkerCompanyCd"
                    :companyCat="companyCatCd"
                    :roleCdList="getRoleCdList(item.checkedCat)"
                    :exceptCdList="selectedUsers"
                    :required="getCheckerRequired(item.checkedCat)"
                    validOnly
                  />
                </template>
                <template v-slot:[`item.checkedDatetime`]="{ item }">
                  {{ toDate(item.checkedDatetime) }}
                </template>
                <template v-slot:[`item.remarks`]="{ item, index }">
                  <TextFieldRef v-if="!canEditChecker(index)" label="" :value="item.remarks" />
                  <TextFieldInput v-else label="" v-model="item.remarks" :maxLength="1000" />
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-form>
</template>

<script>
import { MmmmWfCheckedCat, Role, CompanyCatCd } from '@/enum'
import DateUtils from '@/mixin/date-utils.js'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import UserInput from '@/components/atoms/select/UserInput'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'

export default {
  name: 'MmmmRequestRouteForm',

  mixins: [DateUtils],

  components: {
    TextFieldInput,
    TextFieldRef,
    UserInput,
    SelectSingleInput,
  },

  props: {
    dispositionCd: {
      type: String,
      required: false,
      default: '',
    },
    dispositionReason: {
      type: String,
      required: false,
      default: '',
    },
    checkers: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    outlined: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isEpsonRoute: {
      type: Boolean,
      required: false,
      default: false,
    },
    companyCd: {
      type: String,
      required: false,
      default: '',
    },
    companyCatCd: {
      type: String,
      required: true,
    },
    currentRouteNo: {
      type: Number,
      required: false,
      default: null,
    },
    currentRouteDtlNo: {
      type: Number,
      required: false,
      default: null,
    },
    routeNo: {
      type: Number,
      required: true,
    },
    reqApproveBy: {
      type: Boolean,
      required: false,
      default: true,
    },
    dispositionCdItems: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      headersRoute: [
        { text: '', value: 'by', divider: true, sortable: false, cellClass: 'py-1' },
        {
          text: this.$t('ELE0058'),
          value: 'checkerCd',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        {
          text: this.$t('ELE0234'),
          value: 'checkedDatetime',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        { text: this.$t('ELE0054'), value: 'remarks', sortable: false, cellClass: 'py-1' },
      ],
    }
  },

  computed: {
    enumMmmmWfCheckedCat: () => MmmmWfCheckedCat,
    dispositionCdComputed: {
      get() {
        return this.dispositionCd
      },
      set(value) {
        this.$emit('update:dispositionCd', value)
      },
    },
    dispositionReasonComputed: {
      get() {
        return this.dispositionReason
      },
      set(value) {
        this.$emit('update:dispositionReason', value)
      },
    },
    selectedUsers: {
      // ユーザープルダウンで選択済みの値リスト
      get() {
        let items = []
        for (let idx = 0; idx < this.checkersComputed.length; idx++) {
          items.push({ userCd: this.checkersComputed[idx].checkerCd })
        }
        return items
      },
    },
    checkerCompanyCd: {
      get() {
        let companyCd = this.companyCd
        if (this.companyCatCd == CompanyCatCd.EPSON) {
          companyCd = ''
        }
        return companyCd
      },
    },
    checkersComputed: {
      get() {
        return this.checkers
      },
      set(value) {
        this.$emit('update:checkers', value)
      },
    },
  },

  methods: {
    canEditDisposition() {
      let result = true

      if (this.disabled) {
        result = false
      } else if (!this.isCurrentRoute() || this.currentRouteDtlNo > 1) {
        result = false
      } else {
        result = true
      }
      return result
    },
    canEditChecker(index) {
      let result = true

      if (this.disabled) {
        result = false
      } else if (this.routeNo < this.currentRouteNo) {
        result = false
      } else if (this.isCurrentRoute() && index + 1 < this.currentRouteDtlNo) {
        result = false
      } else {
        result = true
      }
      return result
    },
    isCurrentRoute() {
      return this.routeNo == this.currentRouteNo
    },
    toDate(datetime) {
      return this.toLocalDate(datetime)
    },
    getLabelBy(checkedCat) {
      let label = ''
      if (checkedCat == MmmmWfCheckedCat.REQUEST) {
        label = this.$t('ELE0235')
      } else if (checkedCat == MmmmWfCheckedCat.CHECK) {
        label = this.$t('ELE0236')
      } else if (checkedCat == MmmmWfCheckedCat.APPROVE) {
        label = this.$t('ELE0237')
      }
      return label
    },
    getRoleCdList(checkedCat) {
      let list = []
      if (checkedCat == MmmmWfCheckedCat.CHECK) {
        list = [Role.MMMM_CHECK]
      } else if (checkedCat == MmmmWfCheckedCat.APPROVE) {
        list = [Role.MMMM_APPROVE]
      } else {
        if (this.isEpsonRoute) {
          list = [Role.MMMM_CHECK, Role.MMMM_APPROVE]
        } else {
          list = []
        }
      }
      return list
    },
    getVID(name) {
      return name + '_' + this.routeNo
    },
    getDtlVID(name, idx) {
      return name + '_' + this.routeNo + '_' + idx
    },
    getCheckerRequired(checkedCat) {
      let required = true
      if (checkedCat == MmmmWfCheckedCat.CHECK) {
        required = false
      } else if (checkedCat == MmmmWfCheckedCat.APPROVE && !this.reqApproveBy) {
        required = false
      }
      return required
    },
  },
}
</script>
